import { Form, Formik } from 'formik';
import React, { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';

import { PrimaryButton, ProButton } from '@components/Button/variants';
import ClientRenderedContent from '@components/ClientRenderedContent';
import FieldError from '@components/Form/FieldError';
import TextField from '@components/Form/TextField';
import SectionBreakTitle, { SectionColor } from '@components/SectionBreakTitle';
import { SubscriptionFormProps } from '@components/SubscriptionForm';
import Disclosure from '@components/SubscriptionForm/StripeDisclosure';
import { useSubscribeForm } from '@contexts/SubscribeFormContext';
import { useEventTracking, useSubscriptionUpdateForCbpt } from '@hooks';
import { RECAPTCHA_SITE_KEY } from '@src/env';
import { PaymentElement, useElements, useStripe } from '@stripe/react-stripe-js';
import { StripeError } from '@stripe/stripe-js';
import { ClickTarget, TrackingLocation, click } from '@tracking';
import { buildQueryString, extractQueryParams } from '@utils/queryStringHelper';

interface PaymentMethodProps
  extends Omit<SubscriptionFormProps, 'userEmail' | 'userFullName' | 'dob' | 'formLocation'> {
  isReferral?: boolean;
  subscriptionId?: string;
}

interface FormValues {
  username: string;
}

const PaymentMethod = ({
  buttonText,
  isReferral,
  presentedOfferCodes,
  referringSubscriberName,
  referringSubscriberGuid,
  selectedOffer: { code, currentPrice, billingFrequency, isPro, originalPrice },
  subscriptionId,
}: PaymentMethodProps) => {
  const stripe = useStripe();
  const elements = useElements();
  const reCaptchaRef = useRef<ReCAPTCHA>();
  const [errorMessage, setErrorMessage] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const { mutate: updateSubscriptionForCbpt } = useSubscriptionUpdateForCbpt();
  const protocol = window.location.protocol;
  const host = window.location.host;
  const { article_id, modal } = extractQueryParams(['article_id', 'modal']);
  const hasDiscount = currentPrice !== originalPrice;
  const Button = isPro ? ProButton : PrimaryButton;
  const {
    user: { nextBillingDate, isUserCBPT },
  } = useSubscribeForm();
  const { trackEvent } = useEventTracking({ location: TrackingLocation.PaymentForm });

  const handlePayment = async (): Promise<never | { error: StripeError }> => {
    const queryString = buildQueryString({
      article_id,
      code,
      guid: referringSubscriberGuid,
      modal,
      page: window.location.pathname,
      presented_offer_codes: presentedOfferCodes,
      referer: document.referrer,
      subscription_id: subscriptionId,
    });

    trackEvent(click({ clickTarget: ClickTarget.Subscribe, offerCode: code }));
    const returnUrl = `${protocol}//${host}/payment-status?${queryString}`;
    const paymentResponse = await stripe.confirmPayment({
      elements,
      confirmParams: {
        // Can't use READER_URL for testing, so this is used to build URLs dynamically
        return_url: returnUrl,
      },
      redirect: 'always',
    });

    return { error: paymentResponse.error };
  };

  const handleSubmit = async ({ username }: FormValues) => {
    // Username is a random hidden field to prevent bots from submitting the form, human users will not see this but bots will
    // so if the field is filled out, we know it's a bot and we don't submit the form.
    if (username) {
      return;
    }

    if (isUserCBPT) {
      setIsLoading(true);
      updateSubscriptionForCbpt({
        page: window.location.pathname,
        presented_offer_codes: presentedOfferCodes,
        referer: document.referrer,
        code,
        source: 'web',
      });
      trackEvent(click({ clickTarget: ClickTarget.SubscribeCBPT, offerCode: code }));
      setIsLoading(false);
      return;
    }

    if (!stripe || !elements) {
      setErrorMessage('Still loading Stripe. Please wait a few seconds and then try again.');
      return;
    }

    setIsLoading(true);

    // Trigger form validation and wallet collection
    const { error: submitError } = await elements.submit();
    if (submitError) {
      setErrorMessage(submitError.message);
      setIsLoading(false);
      return;
    }

    const { error } = await handlePayment();

    setIsLoading(false);
    if (error) {
      setErrorMessage(error.message);
      return;
    }
  };

  return (
    <ReCAPTCHA sitekey={RECAPTCHA_SITE_KEY} size="invisible" ref={reCaptchaRef}>
      <Formik initialValues={{ username: '' }} onSubmit={handleSubmit}>
        {() => (
          <Form className="m-auto max-w-md sm:max-w-full">
            <SectionBreakTitle
              title="Payment information"
              titleClassName="text-black"
              color={isPro ? SectionColor.Gold : SectionColor.Magenta}
              className="mb-24"
            />
            <div className="m-auto max-w-[520px]">
              <TextField inputClassName="hidden" name="username" />
              {!isUserCBPT && (
                <div id="payment-form">
                  <PaymentElement
                    onChange={() => setErrorMessage('')}
                    options={{
                      layout: {
                        type: 'tabs',
                        defaultCollapsed: false,
                      },
                    }}
                  />
                  {errorMessage && <FieldError>{errorMessage}</FieldError>}
                </div>
              )}
              <div className="mt-24 border-0 border-b border-t border-solid border-grayscale-beige py-24">
                <div className="flex items-start justify-between">
                  <div className="font-suisse-works text-headline-xxs font-medium text-black">
                    <ClientRenderedContent>
                      {isUserCBPT && nextBillingDate
                        ? `You'll be charged on ${nextBillingDate}`
                        : "Today's Total"}
                    </ClientRenderedContent>
                  </div>
                  <div className="flex flex-col text-right">
                    <span className="font-suisse-works text-headline-xxs font-medium text-black">
                      ${currentPrice.toFixed(2)}
                    </span>
                  </div>
                </div>
                {originalPrice && hasDiscount && billingFrequency && (
                  <div className="mt-8 flex font-suisse-screen text-byline-sm text-grayscale-gray">
                    {billingFrequency} after the offer concludes{' '}
                    <span className="ml-auto">${originalPrice.toFixed(2)}</span>
                  </div>
                )}
              </div>
              {isReferral && (
                <div className="mt-24 text-center font-suisse-screen text-disclosure-sm text-grayscale-gray">
                  By beginning your subscription, we will be sharing your name with{' '}
                  {referringSubscriberName}, who has referred you, and letting them know that
                  you&apos;ve started enjoying The Information.
                </div>
              )}
              <Disclosure />
              <Button
                as="button"
                disabled={!stripe || isLoading}
                isLoading={isLoading}
                fullWidth
                type="submit"
                className="mt-24"
              >
                {buttonText}
              </Button>
            </div>
          </Form>
        )}
      </Formik>
    </ReCAPTCHA>
  );
};

export default PaymentMethod;
